import React, { useState, useEffect, useRef } from 'react';
import { API_URL } from '../config/apiConfig';

interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
  storeId: string;
  [key: string]: any;
}

interface LogsRealTimeProps {
  storeId: string;
}

const LogsRealTime: React.FC<LogsRealTimeProps> = ({ storeId }) => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [autoScroll, setAutoScroll] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll para o final
  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  // Conectar ao SSE
  useEffect(() => {
    console.log('[LogsRealTime] Connecting to SSE...');
    console.log('[LogsRealTime] API_URL:', API_URL);
    console.log('[LogsRealTime] Stream URL:', `${API_URL}/stores/${storeId}/logs/stream`);
    
    // Carregar logs históricos primeiro
    fetch(`${API_URL}/stores/${storeId}/logs`)
      .then(res => res.json())
      .then(historicalLogs => {
        console.log('[LogsRealTime] Historical logs loaded:', historicalLogs.length);
        setLogs(historicalLogs);
      })
      .catch(err => console.error('[LogsRealTime] Error loading historical logs:', err));

    // Conectar ao stream
    const streamUrl = `${API_URL}/stores/${storeId}/logs/stream`;
    console.log('[LogsRealTime] Creating EventSource:', streamUrl);
    const eventSource = new EventSource(streamUrl);
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      setIsConnected(true);
      console.log('[LogsRealTime] SSE Connected successfully!');
    };

    eventSource.onmessage = (event) => {
      console.log('[LogsRealTime] Message received:', event.data);
      try {
        const logEntry: LogEntry = JSON.parse(event.data);
        setLogs(prev => [...prev, logEntry].slice(-100)); // Manter últimos 100
      } catch (error) {
        console.error('[LogsRealTime] Error parsing log:', error);
      }
    };

    eventSource.onerror = (error) => {
      // MAINT-2026-0529-003 (FIX 4): EventSource reconecta sozinho. Só marca como
      // desconectado quando ele realmente desistiu (readyState === CLOSED / 2). Um
      // reconnect transiente (CONNECTING / 0) NÃO deve piscar o badge para vermelho.
      if (eventSource.readyState === EventSource.CLOSED) {
        setIsConnected(false);
      }
      console.error('[LogsRealTime] SSE Error:', error);
      console.error('[LogsRealTime] EventSource readyState:', eventSource.readyState);
    };

    // Cleanup
    return () => {
      eventSource.close();
    };
  }, [storeId]);

  // Limpar logs
  const handleClearLogs = () => {
    setLogs([]);
  };

  // Obter cor baseada no nível do log
  const getLogColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error':
        return 'text-red-600';
      case 'warn':
        return 'text-yellow-600';
      case 'info':
        return 'text-blue-600';
      case 'debug':
        return 'text-gray-600';
      default:
        return 'text-gray-800';
    }
  };

  // Formatar timestamp
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('pt-BR', { hour12: false });
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold">Logs em Tempo Real</h3>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className="text-sm text-gray-600">
              {isConnected ? 'Conectado' : 'Desconectado'}
            </span>
          </div>
          <span className="text-sm text-gray-500">
            {logs.length} logs
          </span>
        </div>
        <div className="flex gap-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
              className="rounded"
            />
            Auto-scroll
          </label>
          <button
            onClick={handleClearLogs}
            className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded"
          >
            Limpar
          </button>
        </div>
      </div>

      <div className="bg-gray-900 rounded p-4 h-96 overflow-y-auto font-mono text-sm">
        {logs.length === 0 ? (
          <div className="text-gray-500 text-center py-8">
            📝 Nenhum log disponível
            <br />
            <span className="text-xs">Filtered Logs: 0</span>
          </div>
        ) : (
          logs.map((log, index) => (
            <div key={index} className="mb-1 hover:bg-gray-800 px-2 py-1 rounded">
              <span className="text-gray-500">{formatTime(log.timestamp)}</span>
              {' - '}
              <span className={getLogColor(log.level)}>{log.level}</span>
              {' - '}
              <span className="text-gray-300">{log.message}</span>
            </div>
          ))
        )}
        <div ref={logsEndRef} />
      </div>
    </div>
  );
};

export default LogsRealTime;
